import React, {
    Fragment,
    useEffect,
    useMemo,
    useState
} from "react";

import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { GlobalSet } from "../../global/GlobalProvider";
import { useTranslation } from "react-i18next";


/* =========================================================
   CONFIG
========================================================= */

const API_KEY =
    "eyJ0eXAiOiJKV1Qidfg%!#%$%LCdf#*&*JhbGciOiJSUzI1NiJ9";

const PER_PAGE = 10;


/* =========================================================
   LEVELS
========================================================= */

const TEAM_LEVELS = [
    {
        id: 1,
        label: "Level 1",
        slug: "level-a"
    },
    {
        id: 2,
        label: "Level 2",
        slug: "level-b"
    },
    {
        id: 3,
        label: "Level 3",
        slug: "level-c"
    },
    {
        id: 4,
        label: "Level 4",
        slug: "level-d"
    },
    {
        id: 5,
        label: "Level 5",
        slug: "level-e"
    },
    {
        id: 6,
        label: "Level 6",
        slug: "level-f"
    },
    {
        id: 7,
        label: "Level 7",
        slug: "level-g"
    },
    {
        id: 8,
        label: "Level 8",
        slug: "level-h"
    },
    {
        id: 9,
        label: "Level 9",
        slug: "level-i"
    }
];


/* =========================================================
   HELPERS
========================================================= */

const numberFormat = (value) => {

    const number =
        Number(value || 0);

    if (
        Number.isNaN(number)
    ) {
        return "0";
    }

    return new Intl.NumberFormat(
        "en-US",
        {
            maximumFractionDigits: 2
        }
    ).format(number);
};


const formatDate = (value) => {

    if (!value) {
        return "—";
    }

    try {

        const normalized =
            String(value).replace(
                " ",
                "T"
            );

        const date =
            new Date(normalized);

        if (
            Number.isNaN(
                date.getTime()
            )
        ) {
            return value;
        }

        return date.toLocaleDateString(
            "en-GB",
            {
                day: "2-digit",
                month: "short",
                year: "numeric"
            }
        );

    } catch (error) {

        return value;
    }
};


const getInitial = (value) => {

    if (!value) {
        return "U";
    }

    return String(value)
        .trim()
        .charAt(0)
        .toUpperCase();
};


/* =========================================================
   COMPONENT
========================================================= */

const BasicDatatable = () => {

    const { id } =
        useParams();

    const { t } =
        useTranslation();

    const { CurrencySymbol } =
        GlobalSet();


    /* =====================================================
       STATES
    ===================================================== */

    const [income, setIncome] =
        useState([]);

    const [loading, setLoading] =
        useState(true);

    const [pageError, setPageError] =
        useState("");

    const [search, setSearch] =
        useState("");

    const [currentPage, setCurrentPage] =
        useState(1);


    /* =====================================================
       CURRENCY
    ===================================================== */

    const currencySymbol =
        "৳" || "৳";


    /* =====================================================
       CURRENT LEVEL
    ===================================================== */

    const currentLevel =
        Number(id) || 0;


    const currentLevelData =
        TEAM_LEVELS.find(
            level =>
                level.id ===
                currentLevel
        );


    /* =====================================================
       FETCH TEAM
    ===================================================== */

    const fetchTeam =
        async () => {

            const selectedCurrency =
                localStorage.getItem(
                    "selectedCurrency"
                );

            const authToken =
                localStorage.getItem(
                    "auth_token"
                );


            if (!authToken) {

                setLoading(false);

                setPageError(
                    "Your login session was not found. Please login again."
                );

                return;
            }


            const data = {

                key_s:
                    API_KEY,

                currency:
                    selectedCurrency,

                pg_id:
                    id

            };


            try {

                setLoading(true);

                setPageError("");


                const response =
                    await axios.post(
                        "/smart-team-level",
                        data
                    );


                const rows =
                    response?.data?.data;


                setIncome(
                    Array.isArray(rows)
                        ? rows
                        : []
                );


            } catch (error) {

                console.error(
                    "Smart Team Error:",
                    error
                );


                setIncome([]);


                setPageError(
                    error?.response
                        ?.data
                        ?.message ||
                    "Unable to load team information. Please try again."
                );


            } finally {

                setLoading(false);
            }
        };


    /* =====================================================
       LOAD
    ===================================================== */

    useEffect(() => {

        setCurrentPage(1);

        setSearch("");

        fetchTeam();

        // eslint-disable-next-line
    }, [id]);


    /* =====================================================
       FILTER
    ===================================================== */

    const filteredIncome =
        useMemo(() => {

            const keyword =
                search
                    .trim()
                    .toLowerCase();


            if (!keyword) {
                return income;
            }


            return income.filter(
                row => {

                    const text = [

                        row.id,

                        row.email,

                        row.count_active_spons,

                        row.total_invest,

                        row.team_and_spon_sell,

                        row.team_sell,

                        row.created_at

                    ]
                        .filter(
                            value =>
                                value !== null &&
                                value !== undefined
                        )
                        .join(" ")
                        .toLowerCase();


                    return text.includes(
                        keyword
                    );
                }
            );

        }, [
            income,
            search
        ]);


    useEffect(() => {

        setCurrentPage(1);

    }, [search]);


    /* =====================================================
       SUMMARY
    ===================================================== */

    const summary =
        useMemo(() => {

            return income.reduce(
                (result, row) => {

                    result.members += 1;

                    result.directMembers +=
                        Number(
                            row.count_active_spons ||
                            0
                        );

                    result.selfSales +=
                        Number(
                            row.total_invest ||
                            0
                        );

                    result.teamSales +=
                        Number(
                            row.team_sell ||
                            0
                        );

                    result.totalNetworkSales +=
                        Number(
                            row.team_and_spon_sell ||
                            0
                        );

                    return result;
                },
                {
                    members: 0,
                    directMembers: 0,
                    selfSales: 0,
                    teamSales: 0,
                    totalNetworkSales: 0
                }
            );

        }, [income]);


    /* =====================================================
       PAGINATION
    ===================================================== */

    const totalPages =
        Math.max(
            1,
            Math.ceil(
                filteredIncome.length /
                PER_PAGE
            )
        );


    useEffect(() => {

        if (
            currentPage >
            totalPages
        ) {

            setCurrentPage(
                totalPages
            );
        }

    }, [
        currentPage,
        totalPages
    ]);


    const paginatedIncome =
        useMemo(() => {

            const start =
                (
                    currentPage -
                    1
                ) *
                PER_PAGE;


            return filteredIncome.slice(
                start,
                start + PER_PAGE
            );

        }, [
            filteredIncome,
            currentPage
        ]);


    const pageNumbers =
        useMemo(() => {

            let start =
                Math.max(
                    1,
                    currentPage - 2
                );

            let end =
                Math.min(
                    totalPages,
                    currentPage + 2
                );


            if (
                end - start < 4
            ) {

                if (
                    start === 1
                ) {

                    end =
                        Math.min(
                            totalPages,
                            5
                        );

                } else {

                    start =
                        Math.max(
                            1,
                            totalPages - 4
                        );
                }
            }


            const pages = [];


            for (
                let i = start;
                i <= end;
                i++
            ) {

                pages.push(i);
            }


            return pages;

        }, [
            currentPage,
            totalPages
        ]);


    const goToPage =
        page => {

            if (
                page < 1 ||
                page > totalPages
            ) {
                return;
            }


            setCurrentPage(page);


            window.scrollTo({
                top: 0,
                behavior: "smooth"
            });
        };


    const showingFrom =
        filteredIncome.length === 0
            ? 0
            : (
                (
                    currentPage -
                    1
                ) *
                PER_PAGE
            ) + 1;


    const showingTo =
        Math.min(
            currentPage *
            PER_PAGE,
            filteredIncome.length
        );


    /* =====================================================
       RENDER
    ===================================================== */

    return (

        <Fragment>


            {/* =================================================
                STYLE
            ================================================= */}

            <style>{`

                * {
                    box-sizing: border-box;
                }


                .smart-team-page {

                    --primary: #5b4df7;
                    --primary-dark: #4939e8;
                    --primary-soft: #f1efff;

                    --green: #12b76a;
                    --green-dark: #067647;
                    --green-soft: #ecfdf3;

                    --blue: #1570ef;
                    --blue-dark: #175cd3;
                    --blue-soft: #eff8ff;

                    --orange: #f79009;
                    --orange-dark: #b54708;
                    --orange-soft: #fff7ed;

                    --red: #f04438;
                    --red-soft: #fef3f2;

                    --text: #101828;
                    --text2: #344054;
                    --muted: #667085;

                    --border: #e4e7ec;
                    --light: #f9fafb;

                    width: 100%;

                    padding: 20px;

                    border-radius: 20px;

                    background: #ffffff;

                    color: var(--text);

                    font-family:
                        Inter,
                        -apple-system,
                        BlinkMacSystemFont,
                        "Segoe UI",
                        Arial,
                        sans-serif;
                }


                .smart-team-container {

                    width: 100%;

                    max-width: 1500px;

                    margin: 0 auto;
                }


                /* =============================================
                   HEADER
                ============================================= */

                .smart-team-header {

                    display: flex;

                    align-items: center;

                    justify-content:
                        space-between;

                    gap: 20px;

                    margin-bottom: 20px;
                }


                .smart-team-heading {

                    display: flex;

                    align-items: center;

                    gap: 14px;
                }


                .smart-team-heading-icon {

                    width: 52px;
                    height: 52px;

                    display: flex;

                    align-items: center;

                    justify-content: center;

                    flex-shrink: 0;

                    border-radius: 15px;

                    background:
                        var(--primary-soft);

                    color:
                        var(--primary);

                    font-size: 21px;
                }


                .smart-team-heading h2 {

                    margin:
                        0 0 4px;

                    color:
                        var(--text);

                    font-size: 27px;

                    line-height: 1.2;

                    font-weight: 800;
                }


                .smart-team-heading p {

                    margin: 0;

                    color:
                        var(--muted);

                    font-size: 12px;
                }


                .smart-team-current-level {

                    display: inline-flex;

                    align-items: center;

                    gap: 7px;

                    padding:
                        9px 13px;

                    border:
                        1px solid
                        #d9d6fe;

                    border-radius: 10px;

                    background:
                        var(--primary-soft);

                    color:
                        var(--primary);

                    font-size: 11px;

                    font-weight: 800;
                }


                /* =============================================
                   LEVEL NAVIGATION
                ============================================= */

                .team-level-navigation {

                    position: relative;

                    margin-bottom: 18px;

                    padding: 7px;

                    overflow-x: auto;

                    border:
                        1px solid
                        var(--border);

                    border-radius: 14px;

                    background:
                        #ffffff;

                    box-shadow:
                        0 4px 16px
                        rgba(
                            16,
                            24,
                            40,
                            .035
                        );

                    scrollbar-width: thin;
                }


                .team-level-navigation::-webkit-scrollbar {

                    height: 5px;
                }


                .team-level-navigation::-webkit-scrollbar-thumb {

                    border-radius: 20px;

                    background:
                        #d0d5dd;
                }


                .team-level-nav-inner {

                    display: flex;

                    align-items: center;

                    gap: 6px;

                    min-width: max-content;
                }


                .team-level-link {

                    min-height: 41px;

                    display: inline-flex;

                    align-items: center;

                    justify-content: center;

                    gap: 7px;

                    padding:
                        0 14px;

                    border-radius: 9px;

                    color:
                        var(--text2) !important;

                    font-size: 11px;

                    font-weight: 700;

                    text-decoration:
                        none !important;

                    transition:
                        .15s ease;
                }


                .team-level-link:hover {

                    background:
                        var(--primary-soft);

                    color:
                        var(--primary) !important;
                }


                .team-level-link.active {

                    background:
                        var(--primary);

                    color:
                        #ffffff !important;

                    box-shadow:
                        0 4px 12px
                        rgba(
                            91,
                            77,
                            247,
                            .18
                        );
                }


                .team-level-all {

                    border-right:
                        1px solid
                        var(--border);

                    padding-right: 8px;

                    margin-right: 2px;
                }


                /* =============================================
                   SUMMARY CARDS
                ============================================= */

                .smart-team-summary {

                    display: grid;

                    grid-template-columns:
                        repeat(
                            4,
                            minmax(0, 1fr)
                        );

                    gap: 13px;

                    margin-bottom: 18px;
                }


                .team-summary-card {

                    position: relative;

                    overflow: hidden;

                    padding: 17px;

                    border:
                        1px solid
                        var(--border);

                    border-radius: 15px;

                    background:
                        #ffffff;

                    box-shadow:
                        0 5px 20px
                        rgba(
                            16,
                            24,
                            40,
                            .04
                        );
                }


                .team-summary-top {

                    display: flex;

                    align-items: center;

                    justify-content:
                        space-between;

                    gap: 10px;

                    margin-bottom: 12px;
                }


                .team-summary-label {

                    color:
                        var(--muted);

                    font-size: 11px;

                    font-weight: 700;
                }


                .team-summary-icon {

                    width: 39px;
                    height: 39px;

                    display: flex;

                    align-items: center;

                    justify-content: center;

                    border-radius: 11px;

                    font-size: 15px;
                }


                .team-summary-icon.members {

                    background:
                        var(--primary-soft);

                    color:
                        var(--primary);
                }


                .team-summary-icon.direct {

                    background:
                        var(--blue-soft);

                    color:
                        var(--blue);
                }


                .team-summary-icon.self {

                    background:
                        var(--green-soft);

                    color:
                        var(--green-dark);
                }


                .team-summary-icon.team {

                    background:
                        var(--orange-soft);

                    color:
                        var(--orange-dark);
                }


                .team-summary-value {

                    margin:
                        0 0 4px;

                    color:
                        var(--text);

                    font-size: 22px;

                    line-height: 1.15;

                    font-weight: 800;

                    word-break: break-word;
                }


                .team-summary-note {

                    margin: 0;

                    color:
                        #98a2b3;

                    font-size: 9px;
                }


                /* =============================================
                   TABLE CARD
                ============================================= */

                .smart-team-card {

                    overflow: hidden;

                    border:
                        1px solid
                        var(--border);

                    border-radius: 17px;

                    background:
                        #ffffff;

                    box-shadow:
                        0 6px 25px
                        rgba(
                            16,
                            24,
                            40,
                            .045
                        );
                }


                .smart-team-card-header {

                    display: flex;

                    align-items: center;

                    justify-content:
                        space-between;

                    gap: 20px;

                    padding:
                        18px 20px;

                    border-bottom:
                        1px solid
                        #eef0f4;
                }


                .smart-team-card-title h3 {

                    margin:
                        0 0 4px;

                    color:
                        var(--text);

                    font-size: 17px;

                    font-weight: 800;
                }


                .smart-team-card-title p {

                    margin: 0;

                    color:
                        var(--muted);

                    font-size: 11px;
                }


                /* =============================================
                   SEARCH
                ============================================= */

                .smart-team-search {

                    position: relative;

                    width: 300px;

                    max-width: 100%;
                }


                .smart-team-search i {

                    position: absolute;

                    top: 50%;
                    left: 14px;

                    transform:
                        translateY(-50%);

                    color:
                        #98a2b3;

                    font-size: 12px;

                    pointer-events: none;
                }


                .smart-team-search input {

                    width: 100%;
                    height: 43px;

                    padding:
                        0 14px
                        0 39px;

                    border:
                        1px solid
                        var(--border);

                    border-radius: 10px;

                    outline: none;

                    background:
                        var(--light);

                    color:
                        var(--text);

                    font-size: 12px;

                    transition:
                        .18s ease;
                }


                .smart-team-search input::placeholder {

                    color:
                        #98a2b3;
                }


                .smart-team-search input:focus {

                    border-color:
                        var(--primary);

                    background:
                        #ffffff;

                    box-shadow:
                        0 0 0 4px
                        rgba(
                            91,
                            77,
                            247,
                            .08
                        );
                }


                /* =============================================
                   TABLE
                ============================================= */

                .smart-team-table-wrap {

                    width: 100%;

                    overflow-x: auto;
                }


                .smart-team-table {

                    width: 100%;

                    min-width: 1050px;

                    border-collapse:
                        collapse;
                }


                .smart-team-table thead {

                    background:
                        #f9fafb;
                }


                .smart-team-table th {

                    padding:
                        14px 16px;

                    border-bottom:
                        1px solid
                        var(--border);

                    color:
                        var(--muted);

                    font-size: 10px;

                    font-weight: 800;

                    text-align: left;

                    text-transform:
                        uppercase;

                    letter-spacing:
                        .35px;

                    white-space: nowrap;
                }


                .smart-team-table td {

                    padding:
                        15px 16px;

                    border-bottom:
                        1px solid
                        #f0f1f4;

                    color:
                        var(--text2);

                    font-size: 12px;

                    vertical-align:
                        middle;
                }


                .smart-team-table tbody tr {

                    transition:
                        background .15s ease;
                }


                .smart-team-table tbody tr:hover {

                    background:
                        #fafaff;
                }


                .smart-team-table tbody tr:last-child td {

                    border-bottom: 0;
                }


                /* =============================================
                   SERIAL
                ============================================= */

                .team-sl {

                    width: 31px;
                    height: 31px;

                    display: inline-flex;

                    align-items: center;

                    justify-content: center;

                    border-radius: 9px;

                    background:
                        #f2f4f7;

                    color:
                        var(--text2);

                    font-size: 10px;

                    font-weight: 800;
                }


                /* =============================================
                   MEMBER
                ============================================= */

                .team-member {

                    display: flex;

                    align-items: center;

                    gap: 10px;

                    min-width: 210px;
                }


                .team-member-avatar {

                    width: 38px;
                    height: 38px;

                    display: flex;

                    align-items: center;

                    justify-content: center;

                    flex-shrink: 0;

                    border-radius: 11px;

                    background:
                        var(--primary-soft);

                    color:
                        var(--primary);

                    font-size: 13px;

                    font-weight: 800;
                }


                .team-member-info {

                    min-width: 0;
                }


                .team-member-info strong {

                    display: block;

                    max-width: 220px;

                    overflow: hidden;

                    color:
                        var(--text);

                    font-size: 12px;

                    font-weight: 700;

                    text-overflow:
                        ellipsis;

                    white-space: nowrap;
                }


                .team-member-info span {

                    display: block;

                    margin-top: 2px;

                    color:
                        #98a2b3;

                    font-size: 9px;
                }


                /* =============================================
                   MEMBER COUNT
                ============================================= */

                .team-member-count {

                    display: inline-flex;

                    align-items: center;

                    gap: 6px;

                    padding:
                        6px 9px;

                    border-radius: 8px;

                    background:
                        var(--blue-soft);

                    color:
                        var(--blue-dark);

                    font-size: 10px;

                    font-weight: 800;

                    white-space: nowrap;
                }


                /* =============================================
                   AMOUNT
                ============================================= */

                .team-amount {

                    color:
                        var(--text);

                    font-size: 12px;

                    font-weight: 800;

                    white-space: nowrap;
                }


                .team-amount.self {

                    color:
                        var(--green-dark);
                }


                .team-amount.network {

                    color:
                        var(--primary);
                }


                .team-amount.team {

                    color:
                        var(--orange-dark);
                }


                /* =============================================
                   DATE
                ============================================= */

                .team-date {

                    display: inline-flex;

                    align-items: center;

                    gap: 7px;

                    color:
                        var(--muted);

                    font-size: 10px;

                    white-space: nowrap;
                }


                .team-date-icon {

                    width: 30px;
                    height: 30px;

                    display: inline-flex;

                    align-items: center;

                    justify-content: center;

                    border-radius: 8px;

                    background:
                        #f2f4f7;

                    color:
                        var(--muted);
                }


                /* =============================================
                   STATES
                ============================================= */

                .smart-team-state {

                    padding:
                        70px 20px;

                    text-align: center;
                }


                .smart-team-state-icon {

                    width: 58px;
                    height: 58px;

                    display: flex;

                    align-items: center;

                    justify-content: center;

                    margin:
                        0 auto 14px;

                    border-radius: 16px;

                    background:
                        var(--primary-soft);

                    color:
                        var(--primary);

                    font-size: 21px;
                }


                .smart-team-state h4 {

                    margin:
                        0 0 5px;

                    color:
                        var(--text);

                    font-size: 15px;

                    font-weight: 800;
                }


                .smart-team-state p {

                    max-width: 420px;

                    margin:
                        0 auto;

                    color:
                        var(--muted);

                    font-size: 11px;

                    line-height: 1.6;
                }


                .smart-team-spinner {

                    width: 39px;
                    height: 39px;

                    margin:
                        0 auto 14px;

                    border:
                        4px solid
                        #e8e9ee;

                    border-top-color:
                        var(--primary);

                    border-radius: 50%;

                    animation:
                        teamSpin
                        .8s linear
                        infinite;
                }


                @keyframes teamSpin {

                    to {

                        transform:
                            rotate(360deg);
                    }
                }


                .smart-team-retry {

                    min-height: 39px;

                    margin-top: 15px;

                    padding:
                        0 16px;

                    border: 0;

                    border-radius: 9px;

                    background:
                        var(--primary);

                    color:
                        #ffffff;

                    font-size: 11px;

                    font-weight: 800;

                    cursor: pointer;
                }


                /* =============================================
                   PAGINATION
                ============================================= */

                .smart-team-footer {

                    display: flex;

                    align-items: center;

                    justify-content:
                        space-between;

                    gap: 15px;

                    padding:
                        15px 20px;

                    border-top:
                        1px solid
                        #eef0f4;
                }


                .smart-team-showing {

                    color:
                        var(--muted);

                    font-size: 11px;
                }


                .smart-team-showing strong {

                    color:
                        var(--text2);
                }


                .smart-team-pagination {

                    display: flex;

                    align-items: center;

                    gap: 5px;
                }


                .smart-team-page-button {

                    min-width: 36px;
                    height: 36px;

                    display: flex;

                    align-items: center;

                    justify-content: center;

                    padding:
                        0 9px;

                    border:
                        1px solid
                        var(--border);

                    border-radius: 8px;

                    outline: none;

                    background:
                        #ffffff;

                    color:
                        var(--text2);

                    font-size: 11px;

                    font-weight: 700;

                    cursor: pointer;

                    transition:
                        .15s ease;
                }


                .smart-team-page-button:hover:not(:disabled) {

                    border-color:
                        var(--primary);

                    background:
                        var(--primary-soft);

                    color:
                        var(--primary);
                }


                .smart-team-page-button.active {

                    border-color:
                        var(--primary);

                    background:
                        var(--primary);

                    color:
                        #ffffff;
                }


                .smart-team-page-button:disabled {

                    opacity: .4;

                    cursor: not-allowed;
                }


                /* =============================================
                   MOBILE MEMBER CARDS
                ============================================= */

                .smart-team-mobile {

                    display: none;
                }


                .team-mobile-card {

                    padding: 15px;

                    margin-bottom: 11px;

                    border:
                        1px solid
                        var(--border);

                    border-radius: 13px;

                    background:
                        #ffffff;
                }


                .team-mobile-card:last-child {

                    margin-bottom: 0;
                }


                .team-mobile-header {

                    display: flex;

                    align-items: center;

                    justify-content:
                        space-between;

                    gap: 10px;

                    padding-bottom: 12px;

                    margin-bottom: 12px;

                    border-bottom:
                        1px solid
                        #f0f1f4;
                }


                .team-mobile-grid {

                    display: grid;

                    grid-template-columns:
                        repeat(
                            2,
                            minmax(0,1fr)
                        );

                    gap: 10px;
                }


                .team-mobile-item {

                    padding: 10px;

                    border-radius: 9px;

                    background:
                        #f9fafb;
                }


                .team-mobile-item span {

                    display: block;

                    margin-bottom: 4px;

                    color:
                        var(--muted);

                    font-size: 9px;

                    font-weight: 600;
                }


                .team-mobile-item strong {

                    display: block;

                    color:
                        var(--text);

                    font-size: 11px;

                    font-weight: 800;

                    word-break:
                        break-word;
                }


                .team-mobile-date {

                    display: flex;

                    align-items: center;

                    gap: 6px;

                    padding-top: 12px;

                    margin-top: 12px;

                    border-top:
                        1px solid
                        #f0f1f4;

                    color:
                        var(--muted);

                    font-size: 10px;
                }


                /* =============================================
                   RESPONSIVE
                ============================================= */

                @media (
                    max-width: 1100px
                ) {

                    .smart-team-summary {

                        grid-template-columns:
                            repeat(
                                2,
                                minmax(0,1fr)
                            );
                    }

                }


                @media (
                    max-width: 767px
                ) {

                    .smart-team-page {

                        padding: 13px;

                        border-radius: 14px;
                    }


                    .smart-team-header {

                        align-items:
                            flex-start;

                        flex-direction:
                            column;
                    }


                    .smart-team-heading {

                        align-items:
                            flex-start;
                    }


                    .smart-team-heading-icon {

                        width: 45px;
                        height: 45px;

                        border-radius: 12px;
                    }


                    .smart-team-heading h2 {

                        font-size: 22px;
                    }


                    .smart-team-current-level {

                        width: 100%;

                        justify-content:
                            center;
                    }


                    .smart-team-summary {

                        grid-template-columns:
                            repeat(
                                2,
                                minmax(0,1fr)
                            );

                        gap: 9px;
                    }


                    .team-summary-card {

                        padding: 14px;
                    }


                    .team-summary-value {

                        font-size: 18px;
                    }


                    .smart-team-card-header {

                        align-items:
                            stretch;

                        flex-direction:
                            column;
                    }


                    .smart-team-search {

                        width: 100%;
                    }


                    .smart-team-desktop {

                        display: none;
                    }


                    .smart-team-mobile {

                        display: block;

                        padding: 12px;

                        background:
                            #f9fafb;
                    }


                    .smart-team-footer {

                        align-items:
                            flex-start;

                        flex-direction:
                            column;
                    }


                    .smart-team-pagination {

                        width: 100%;

                        flex-wrap: wrap;

                        justify-content:
                            center;
                    }

                }


                @media (
                    max-width: 480px
                ) {

                    .smart-team-page {

                        padding: 9px;
                    }


                    .smart-team-summary {

                        grid-template-columns:
                            1fr;
                    }


                    .team-mobile-grid {

                        grid-template-columns:
                            1fr 1fr;
                    }

                }

            `}</style>


            {/* =================================================
                PAGE
            ================================================= */}

            <main className="smart-team-page">

                <div className="smart-team-container">


                    {/* =========================================
                        PAGE HEADER
                    ========================================= */}

                    <div className="smart-team-header">

                        <div className="smart-team-heading">

                            <div className="smart-team-heading-icon">

                                <i
                                    className="fa fa-users"
                                    aria-hidden="true"
                                ></i>

                            </div>


                            <div>

                                <h2>
                                    My Smart Team
                                </h2>

                                <p>
                                    View your team members, sales
                                    performance and network activity.
                                </p>

                            </div>

                        </div>


                        {currentLevelData && (

                            <div className="smart-team-current-level">

                                <i
                                    className="fa fa-sitemap"
                                    aria-hidden="true"
                                ></i>

                                Viewing {currentLevelData.label}

                            </div>

                        )}

                    </div>


                    {/* =========================================
                        LEVEL NAVIGATION
                    ========================================= */}

                    <div className="team-level-navigation">

                        <div className="team-level-nav-inner">


                            {/* ALL LEVEL */}

                            <div className="team-level-all">

                                <Link
                                    to="/my-smart-team"
                                    className="team-level-link"
                                >

                                    <i
                                        className="fa fa-th-large"
                                        aria-hidden="true"
                                    ></i>

                                    All Levels

                                </Link>

                            </div>


                            {/* LEVEL 1 - 9 */}

                            {TEAM_LEVELS.map(
                                level => (

                                    <Link
                                        key={level.id}
                                        to={
                                            `/view-team/${level.id}/${level.slug}`
                                        }
                                        className={
                                            `team-level-link ${
                                                currentLevel ===
                                                level.id
                                                    ? "active"
                                                    : ""
                                            }`
                                        }
                                    >

                                        {currentLevel ===
                                            level.id && (

                                            <i
                                                className="fa fa-check-circle"
                                                aria-hidden="true"
                                            ></i>

                                        )}

                                        {level.label}

                                    </Link>

                                )
                            )}

                        </div>

                    </div>


                    {/* =========================================
                        SUMMARY
                    ========================================= */}

                    {!loading &&
                        !pageError && (

                        <div className="smart-team-summary">


                            {/* MEMBERS */}

                            <div className="team-summary-card">

                                <div className="team-summary-top">

                                    <span className="team-summary-label">
                                        Level Members
                                    </span>

                                    <div className="team-summary-icon members">

                                        <i
                                            className="fa fa-users"
                                            aria-hidden="true"
                                        ></i>

                                    </div>

                                </div>


                                <h3 className="team-summary-value">
                                    {numberFormat(
                                        summary.members
                                    )}
                                </h3>

                                <p className="team-summary-note">
                                    Members in this level
                                </p>

                            </div>


                            {/* DIRECT MEMBERS */}

                            <div className="team-summary-card">

                                <div className="team-summary-top">

                                    <span className="team-summary-label">
                                        Active Sponsors
                                    </span>

                                    <div className="team-summary-icon direct">

                                        <i
                                            className="fa fa-user-plus"
                                            aria-hidden="true"
                                        ></i>

                                    </div>

                                </div>


                                <h3 className="team-summary-value">
                                    {numberFormat(
                                        summary.directMembers
                                    )}
                                </h3>

                                <p className="team-summary-note">
                                    Member connections
                                </p>

                            </div>


                            {/* SELF SALES */}

                            <div className="team-summary-card">

                                <div className="team-summary-top">

                                    <span className="team-summary-label">
                                        Self Sales
                                    </span>

                                    <div className="team-summary-icon self">

                                        <i
                                            className="fa fa-line-chart"
                                            aria-hidden="true"
                                        ></i>

                                    </div>

                                </div>


                                <h3 className="team-summary-value">

                                    {currencySymbol}{" "}

                                    {numberFormat(
                                        summary.selfSales
                                    )}

                                </h3>

                                <p className="team-summary-note">
                                    Total self sales
                                </p>

                            </div>


                            {/* TEAM SALES */}

                            <div className="team-summary-card">

                                <div className="team-summary-top">

                                    <span className="team-summary-label">
                                        Team Sales
                                    </span>

                                    <div className="team-summary-icon team">

                                        <i
                                            className="fa fa-bar-chart"
                                            aria-hidden="true"
                                        ></i>

                                    </div>

                                </div>


                                <h3 className="team-summary-value">

                                    {currencySymbol}{" "}

                                    {numberFormat(
                                        summary.teamSales
                                    )}

                                </h3>

                                <p className="team-summary-note">
                                    Total team sales
                                </p>

                            </div>

                        </div>

                    )}


                    {/* =========================================
                        TEAM LIST CARD
                    ========================================= */}

                    <div className="smart-team-card">


                        {/* HEADER */}

                        <div className="smart-team-card-header">

                            <div className="smart-team-card-title">

                                <h3>

                                    {currentLevelData
                                        ? `${currentLevelData.label} Members`
                                        : "Team Members"
                                    }

                                </h3>

                                <p>
                                    Detailed member and sales
                                    information for this level.
                                </p>

                            </div>


                            {!loading &&
                                !pageError &&
                                income.length > 0 && (

                                <div className="smart-team-search">

                                    <i
                                        className="fa fa-search"
                                        aria-hidden="true"
                                    ></i>

                                    <input
                                        type="text"
                                        value={search}
                                        placeholder="Search member..."
                                        onChange={
                                            e =>
                                                setSearch(
                                                    e.target.value
                                                )
                                        }
                                    />

                                </div>

                            )}

                        </div>


                        {/* =====================================
                            LOADING
                        ===================================== */}

                        {loading ? (

                            <div className="smart-team-state">

                                <div className="smart-team-spinner"></div>

                                <h4>
                                    Loading team members...
                                </h4>

                                <p>
                                    Please wait while we load
                                    your smart team information.
                                </p>

                            </div>

                        ) : pageError ? (


                            /* =================================
                               ERROR
                            ================================= */

                            <div className="smart-team-state">

                                <div className="smart-team-state-icon">

                                    <i
                                        className="fa fa-exclamation-triangle"
                                        aria-hidden="true"
                                    ></i>

                                </div>


                                <h4>
                                    Unable to load team
                                </h4>


                                <p>
                                    {pageError}
                                </p>


                                <button
                                    type="button"
                                    className="smart-team-retry"
                                    onClick={fetchTeam}
                                >
                                    Try Again
                                </button>

                            </div>

                        ) : paginatedIncome.length === 0 ? (


                            /* =================================
                               EMPTY
                            ================================= */

                            <div className="smart-team-state">

                                <div className="smart-team-state-icon">

                                    <i
                                        className="fa fa-users"
                                        aria-hidden="true"
                                    ></i>

                                </div>


                                <h4>

                                    {search
                                        ? "No matching members found"
                                        : "No members found"
                                    }

                                </h4>


                                <p>

                                    {search
                                        ? "No team member matches your current search."
                                        : "There are currently no members available in this level."
                                    }

                                </p>

                            </div>

                        ) : (

                            <Fragment>


                                {/* =================================
                                    DESKTOP TABLE
                                ================================= */}

                                <div className="smart-team-desktop">

                                    <div className="smart-team-table-wrap">

                                        <table className="smart-team-table">

                                            <thead>

                                                <tr>

                                                    <th>
                                                        SL
                                                    </th>

                                                    <th>
                                                        {t(
                                                            "pro_his_bet_type"
                                                        ) || "Member"}
                                                    </th>

                                                    <th>
                                                        {t(
                                                            "pro_his_odds_name"
                                                        ) || "Members"}
                                                    </th>

                                                    <th>
                                                        Self Sell
                                                    </th>

                                                    <th>
                                                        Self And Team
                                                    </th>

                                                    <th>
                                                        Team Sells
                                                    </th>

                                                    <th>
                                                        Join Date
                                                    </th>

                                                </tr>

                                            </thead>


                                            <tbody>

                                                {paginatedIncome.map(
                                                    (
                                                        row,
                                                        index
                                                    ) => {

                                                        const serial =
                                                            (
                                                                currentPage -
                                                                1
                                                            ) *
                                                            PER_PAGE +
                                                            index +
                                                            1;


                                                        return (

                                                            <tr
                                                                key={
                                                                    row.id ||
                                                                    `${row.email}-${serial}`
                                                                }
                                                            >


                                                                {/* SL */}

                                                                <td>

                                                                    <span className="team-sl">
                                                                        {serial}
                                                                    </span>

                                                                </td>


                                                                {/* EMAIL / MEMBER */}

                                                                <td>

                                                                    <div className="team-member">

                                                                        <div className="team-member-avatar">

                                                                            {getInitial(
                                                                                row.email
                                                                            )}

                                                                        </div>


                                                                        <div className="team-member-info">

                                                                            <strong
                                                                                title={
                                                                                    row.email
                                                                                }
                                                                            >
                                                                                {row.email ||
                                                                                    "—"}
                                                                            </strong>

                                                                            <span>
                                                                                Team Member
                                                                            </span>

                                                                        </div>

                                                                    </div>

                                                                </td>


                                                                {/* MEMBERS */}

                                                                <td>

                                                                    <span className="team-member-count">

                                                                        <i
                                                                            className="fa fa-users"
                                                                            aria-hidden="true"
                                                                        ></i>

                                                                        {numberFormat(
                                                                            row.count_active_spons
                                                                        )}{" "}
                                                                        Members

                                                                    </span>

                                                                </td>


                                                                {/* SELF SELL */}

                                                                <td>

                                                                    <span className="team-amount self">

                                                                        {currencySymbol}{" "}

                                                                        {numberFormat(
                                                                            row.total_invest
                                                                        )}

                                                                    </span>

                                                                </td>


                                                                {/* SELF + TEAM */}

                                                                <td>

                                                                    <span className="team-amount network">

                                                                        {currencySymbol}{" "}

                                                                        {numberFormat(
                                                                            row.team_and_spon_sell
                                                                        )}

                                                                    </span>

                                                                </td>


                                                                {/* TEAM SELL */}

                                                                <td>

                                                                    <span className="team-amount team">

                                                                        {currencySymbol}{" "}

                                                                        {numberFormat(
                                                                            row.team_sell
                                                                        )}

                                                                    </span>

                                                                </td>


                                                                {/* JOIN DATE */}

                                                                <td>

                                                                    <div className="team-date">

                                                                        <span className="team-date-icon">

                                                                            <i
                                                                                className="fa fa-calendar"
                                                                                aria-hidden="true"
                                                                            ></i>

                                                                        </span>

                                                                        {formatDate(
                                                                            row.created_at
                                                                        )}

                                                                    </div>

                                                                </td>

                                                            </tr>

                                                        );
                                                    }
                                                )}

                                            </tbody>

                                        </table>

                                    </div>

                                </div>


                                {/* =================================
                                    MOBILE
                                ================================= */}

                                <div className="smart-team-mobile">

                                    {paginatedIncome.map(
                                        (
                                            row,
                                            index
                                        ) => {

                                            const serial =
                                                (
                                                    currentPage -
                                                    1
                                                ) *
                                                PER_PAGE +
                                                index +
                                                1;


                                            return (

                                                <div
                                                    className="team-mobile-card"
                                                    key={
                                                        row.id ||
                                                        `${row.email}-${serial}`
                                                    }
                                                >


                                                    {/* HEADER */}

                                                    <div className="team-mobile-header">

                                                        <div className="team-member">

                                                            <div className="team-member-avatar">

                                                                {getInitial(
                                                                    row.email
                                                                )}

                                                            </div>


                                                            <div className="team-member-info">

                                                                <strong>
                                                                    {row.email ||
                                                                        "—"}
                                                                </strong>

                                                                <span>
                                                                    Member #{serial}
                                                                </span>

                                                            </div>

                                                        </div>


                                                        <span className="team-member-count">

                                                            {numberFormat(
                                                                row.count_active_spons
                                                            )}

                                                            <i
                                                                className="fa fa-users"
                                                                aria-hidden="true"
                                                            ></i>

                                                        </span>

                                                    </div>


                                                    {/* VALUES */}

                                                    <div className="team-mobile-grid">


                                                        <div className="team-mobile-item">

                                                            <span>
                                                                Self Sell
                                                            </span>

                                                            <strong>

                                                                {currencySymbol}{" "}

                                                                {numberFormat(
                                                                    row.total_invest
                                                                )}

                                                            </strong>

                                                        </div>


                                                        <div className="team-mobile-item">

                                                            <span>
                                                                Team Sells
                                                            </span>

                                                            <strong>

                                                                {currencySymbol}{" "}

                                                                {numberFormat(
                                                                    row.team_sell
                                                                )}

                                                            </strong>

                                                        </div>


                                                        <div className="team-mobile-item">

                                                            <span>
                                                                Self And Team
                                                            </span>

                                                            <strong>

                                                                {currencySymbol}{" "}

                                                                {numberFormat(
                                                                    row.team_and_spon_sell
                                                                )}

                                                            </strong>

                                                        </div>


                                                        <div className="team-mobile-item">

                                                            <span>
                                                                Members
                                                            </span>

                                                            <strong>

                                                                {numberFormat(
                                                                    row.count_active_spons
                                                                )}

                                                            </strong>

                                                        </div>

                                                    </div>


                                                    {/* DATE */}

                                                    <div className="team-mobile-date">

                                                        <i
                                                            className="fa fa-calendar"
                                                            aria-hidden="true"
                                                        ></i>

                                                        Joined{" "}

                                                        {formatDate(
                                                            row.created_at
                                                        )}

                                                    </div>

                                                </div>

                                            );
                                        }
                                    )}

                                </div>

                            </Fragment>
                        )}


                        {/* =====================================
                            FOOTER / PAGINATION
                        ===================================== */}

                        {!loading &&
                            !pageError &&
                            filteredIncome.length > 0 && (

                            <div className="smart-team-footer">


                                {/* SHOWING */}

                                <div className="smart-team-showing">

                                    Showing{" "}

                                    <strong>
                                        {showingFrom}
                                    </strong>

                                    {" "}to{" "}

                                    <strong>
                                        {showingTo}
                                    </strong>

                                    {" "}of{" "}

                                    <strong>
                                        {filteredIncome.length}
                                    </strong>

                                    {" "}members

                                </div>


                                {/* PAGINATION */}

                                <div className="smart-team-pagination">


                                    {/* FIRST */}

                                    <button
                                        type="button"
                                        className="smart-team-page-button"
                                        disabled={
                                            currentPage === 1
                                        }
                                        onClick={() =>
                                            goToPage(1)
                                        }
                                    >

                                        <i
                                            className="fa fa-angle-double-left"
                                            aria-hidden="true"
                                        ></i>

                                    </button>


                                    {/* PREVIOUS */}

                                    <button
                                        type="button"
                                        className="smart-team-page-button"
                                        disabled={
                                            currentPage === 1
                                        }
                                        onClick={() =>
                                            goToPage(
                                                currentPage -
                                                1
                                            )
                                        }
                                    >

                                        <i
                                            className="fa fa-angle-left"
                                            aria-hidden="true"
                                        ></i>

                                    </button>


                                    {/* PAGE NUMBERS */}

                                    {pageNumbers.map(
                                        page => (

                                            <button
                                                type="button"
                                                key={page}
                                                className={
                                                    `smart-team-page-button ${
                                                        currentPage ===
                                                        page
                                                            ? "active"
                                                            : ""
                                                    }`
                                                }
                                                onClick={() =>
                                                    goToPage(
                                                        page
                                                    )
                                                }
                                            >
                                                {page}
                                            </button>

                                        )
                                    )}


                                    {/* NEXT */}

                                    <button
                                        type="button"
                                        className="smart-team-page-button"
                                        disabled={
                                            currentPage ===
                                            totalPages
                                        }
                                        onClick={() =>
                                            goToPage(
                                                currentPage +
                                                1
                                            )
                                        }
                                    >

                                        <i
                                            className="fa fa-angle-right"
                                            aria-hidden="true"
                                        ></i>

                                    </button>


                                    {/* LAST */}

                                    <button
                                        type="button"
                                        className="smart-team-page-button"
                                        disabled={
                                            currentPage ===
                                            totalPages
                                        }
                                        onClick={() =>
                                            goToPage(
                                                totalPages
                                            )
                                        }
                                    >

                                        <i
                                            className="fa fa-angle-double-right"
                                            aria-hidden="true"
                                        ></i>

                                    </button>

                                </div>

                            </div>

                        )}

                    </div>

                </div>

            </main>

        </Fragment>
    );
};


export default BasicDatatable;